import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';

import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';

import { MonthAttribution } from 'src/models/monthly-finance.model';

import { translations } from 'src/shared/constants/language-translations';

interface AttributionOption {
  value: MonthAttribution;
  label: string;
  /** Where the label's translation lives, when its English words already mean something else. */
  translationKey?: string;
  tooltip: string;
}

const OPTIONS: AttributionOption[] = [
  {
    value: 'accrual',
    label: 'Accrual',
    tooltip: 'Counts each amount in the months it covers. A stay from Aug 29 to Oct 31 splits across August, September and October.',
  },
  {
    value: 'cash',
    label: 'Cash',
    // The 'Cash' entry is the payment method ("Dinheiro"); the accounting basis is "Caixa".
    translationKey: 'Cash (accounting basis)',
    tooltip: 'Counts each amount whole, in the month the money moved. The same stay counts in full on the day it was paid.',
  },
];

function translatedLabel(option: AttributionOption): string {
  const language = localStorage.getItem('language');
  if (!language || language === 'en') { return option.label; }
  return translations[option.translationKey ?? option.label]?.[language] || option.label;
}

/** The Accrual / Cash pill on the Monthly P&L, a month in Transactions, and the Overview. */
@Component({
  selector: 'tidy-month-attribution-switch',
  templateUrl: './month-attribution-switch.component.html',
  styleUrls: ['./month-attribution-switch.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [MatTooltipModule, TidyUiComponentsModule, SharedModule],
})
export class MonthAttributionSwitchComponent {
  readonly value = input.required<MonthAttribution>();
  /** Greys the switch out, with this as its tooltip, where the reading changes nothing. */
  readonly disabledReason = input<string | null>(null);
  readonly valueChange = output<MonthAttribution>();

  readonly options = OPTIONS.map(option => ({ ...option, shownLabel: translatedLabel(option) }));

  pick(value: MonthAttribution): void {
    if (this.disabledReason() || value === this.value()) { return; }
    this.valueChange.emit(value);
  }
}
